import React, { useState, useEffect } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { formatDateTime } from '../../../Utils/dateFormat';
import { fetchFacilityAudit } from '../../../Apis/Facility';
import Spinner from '../../CustomComponents/Spinner';
import {
  DetailColumn,
  DetailBar,
  DetailTitleWrap,
  DetailTitle,
  Acts,
  DetailCloseButton,
  StatusBadge,
  HeaderCard,
  FactGrid,
  FactKey,
  FactVal,
  Panel,
  EmptyState
} from './AuditDetailStyles';

const GRID = '1fr 1fr';
const headStyle = {
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 12,
  padding: '8px 0',
  borderBottom: '1px solid #E8ECF0',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#525E6F'
};
const rowStyle = {
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 12,
  padding: '10px 0',
  borderBottom: '1px solid #F0F2F5',
  fontSize: 13,
  color: '#1A2332',
  alignItems: 'center'
};

const Fact = ({ label, value }) => (
  <div>
    <FactKey>{label}</FactKey>
    <FactVal>{value || '—'}</FactVal>
  </div>
);

/**
 * Facility audit detail (audit split view). Restyles the old drawer content:
 * a fact card plus the facility's enable/disable status-change history
 * (fetchFacilityAudit).
 */
const AuditFacilityDetail = ({ facility, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [log, setLog] = useState([]);

  const facilityId = facility?.facilityId;

  useEffect(() => {
    if (!facilityId) return;
    setLoading(true);
    setLog([]);
    fetchFacilityAudit(facilityId)
      .then(res => {
        if (res.data.success) setLog(res.data.result || []);
      })
      .catch(err => console.log(err))
      .finally(() => setLoading(false));
  }, [facilityId]);

  if (!facility) {
    return (
      <DetailColumn>
        <EmptyState style={{ paddingTop: 80 }}>
          Select a facility from the list to view its audit log.
        </EmptyState>
      </DetailColumn>
    );
  }

  return (
    <DetailColumn>
      <DetailBar>
        <DetailTitleWrap>
          <DetailTitle>{facility.facilityName || 'Facility'}</DetailTitle>
          <StatusBadge status={facility.disabled ? 'Failed' : 'Completed'}>
            {facility.disabled ? 'Disabled' : 'Enabled'}
          </StatusBadge>
        </DetailTitleWrap>
        <Acts>
          <DetailCloseButton
            type='button'
            aria-label='Close facility details'
            onClick={onClose}
          >
            <CloseOutlined />
          </DetailCloseButton>
        </Acts>
      </DetailBar>

      <HeaderCard>
        <FactGrid>
          <Fact label='Facility Name' value={facility.facilityName} />
          <Fact
            label='Facility Status'
            value={facility.disabled ? 'Disabled' : 'Enabled'}
          />
          <Fact
            label='Last Update'
            value={
              facility.facilityDisableStatusLastUpdated
                ? formatDateTime(facility.facilityDisableStatusLastUpdated)
                : '—'
            }
          />
        </FactGrid>
      </HeaderCard>

      <Panel>
        <h3>Status history</h3>
        <p className='sub'>Facility enable / disable changes.</p>
        {loading ? (
          <Spinner height={'180px'} />
        ) : log.length ? (
          <div>
            <div style={headStyle}>
              <span>Update Time</span>
              <span>Status</span>
            </div>
            {log.map((d, i) => (
              <div key={i} style={rowStyle}>
                <span>{formatDateTime(d.updateDate)}</span>
                <span>
                  <StatusBadge status={d.status === 'ENABLE' ? 'Completed' : 'Failed'}>
                    {d.status}
                  </StatusBadge>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState>No status changes recorded.</EmptyState>
        )}
      </Panel>
    </DetailColumn>
  );
};

export default AuditFacilityDetail;
